import { useInfo } from "@/context/InfoProvider/InfoProvider";
import { useLogout } from "@/hooks/useLogout";
import { SettingsOutlined } from "@mui/icons-material";
import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useState } from "react";
import { useLocation } from "react-router-dom";
import { LinkType } from "@/Components/v2/constants/enum";
import { User } from "@/Components/Icons/User";
import { hasAccess } from "@/Utils/ShowComponent";
import Logout from "@/Components/Icons/Logout";
import { Menu } from "@/Components/v2/atoms/Menu";
import { Stack } from "@/Components/v2/atoms/Stack";
import { SettingMenuItem } from "@/Components/v2/molecule/SettingMenuItem";
import { ThemeToggle } from "@/Components/v2/molecule/ThemeToggle";
import { Tooltip } from "../../atoms/ToolTip";

interface MenuListItem {
  id: string;
  label: string;
  route: string;
  activePaths: string[];
  onClick?: () => void;
  hasAccess: boolean;
  isBeta?: boolean;
  type?: LinkType;
  icon?: React.ReactNode;
}

const SettingsMenuIcon = () => {
  const theme = useTheme();
  const location = useLocation();
  const { logout } = useLogout();
  const { userData: user } = useInfo();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const handleSettingsClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setIsOpen(true);
  };
  const handleSettingsClose = () => {
    setAnchorEl(null);
    setIsOpen(false);
  };

  const settingsMenuTopItems: MenuListItem[] = [
    {
      id: "team-management",
      label: "Team Management",
      route: "/team-management",
      activePaths: ["/team-management"],
      icon: <User fontSize="small" />,
      hasAccess: hasAccess(user, "Admin"),
      type: LinkType.Internal,
    },
  ];

  const settingsMenuBottomItems: MenuListItem[] = [
    {
      id: "logout",
      label: "Log Out",
      route: "#",
      activePaths: [],
      onClick: logout,
      icon: <Logout fontSize="small" />,
      hasAccess: true,
      type: LinkType.Internal,
    },
  ];

  const isSelected = (routes: string[]) => {
    return routes.includes(location.pathname);
  };

  return (
    <Box sx={{ marginRight: "10px" }}>
      <Box
        aria-controls={isOpen ? "settings-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={isOpen ? "true" : undefined}
        onClick={handleSettingsClick as () => void}
        sx={{
          cursor: "pointer",
          backgroundColor: isOpen ? theme.palette.active.main : "transparent",
          padding: "8px 10px",
          borderRadius: "4px",
        }}
      >
        <Tooltip title="GrowthOS Settings" placement="top">
          <SettingsOutlined
            sx={{
              width: "1.5rem",
              height: "1.5rem",
            }}
          />
        </Tooltip>
      </Box>
      <Menu
        elevation={3}
        sx={{
          padding: 0,
          marginTop: 5,
        }}
        id="settings-menu"
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        open={isOpen}
        onClose={handleSettingsClose}
      >
        <Stack>
          {settingsMenuTopItems.map((item) => {
            return (
              <SettingMenuItem key={item.id} selected={isSelected(item.activePaths)} {...item} />
            );
          })}
          <ThemeToggle />
          {settingsMenuBottomItems.map((item) => {
            return (
              <SettingMenuItem key={item.id} selected={isSelected(item.activePaths)} {...item} />
            );
          })}
        </Stack>
      </Menu>
    </Box>
  );
};

export default SettingsMenuIcon;
